import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getProgramDashboardReportCounts, getReportYears, getProgramDashboardReportStatusCounts, getProgramDashboardOrganizationCounts } from '../services/programLambda';
import { BASE_STORAGE_KEY } from '@/utils/constants';

const STORAGE_KEY = `${BASE_STORAGE_KEY}:dashboard`;

/**
 * Pinia store for program dashboard. Provides state and actions for fetching and tracking dashboard data.
 * @typedef {Object} ProgramDashboardStore
 * @property {Object|null} reportCounts - Dashboard report counts.
 * @property {Array<Object>} availableYears - List of available report years.
 * @property {Object|null} reportStatusCounts - Status counts for reports.
 * @property {Object|null} organizationCounts - Organization counts.
 * @property {number|null} selectedReportYear - The selected report year.
 * @property {function(): Promise<void>} fetch - Fetches available report years and dashboard counts for the selected year.
 * @property {function(): Promise<void>} fetchReportStatusCounts - Fetches report status counts for the selected report year.
 */
export const useProgramDashboardStore = defineStore(STORAGE_KEY, () => {
  // State
  const reportCounts = ref(null);
  const availableYears = ref([]);
  const reportStatusCounts = ref(null);
  const organizationCounts = ref(null);
  const selectedReportYear = ref(null);


  // Actions
  /**
   * Fetches available report years and dashboard counts for the selected year.
   * @returns {Promise<void>}
   */
  async function fetch() {    
    const reportYears = await getReportYears();
    availableYears.value = reportYears.map(year => ({
      label: year.reportYear,
      value: year.reportYear,
    }));

    if (availableYears.value.length > 0) {
      selectedReportYear.value = availableYears.value[0].value;
      const [counts, orgCounts, statusCounts] = await Promise.all([
        getProgramDashboardReportCounts(),
        getProgramDashboardOrganizationCounts(),
        getProgramDashboardReportStatusCounts(selectedReportYear.value)
      ]);
      reportCounts.value = counts;
      organizationCounts.value = orgCounts;
      reportStatusCounts.value = statusCounts;
    }
  }

  /**
   * Fetches report status counts for the selected report year.
   * @returns {Promise<void>}
   */
  async function fetchReportStatusCounts() {
    reportStatusCounts.value = await getProgramDashboardReportStatusCounts(selectedReportYear.value);
  }

  return {
    reportCounts,
    availableYears,
    reportStatusCounts,
    organizationCounts,
    selectedReportYear,

    fetch,
    fetchReportStatusCounts,
  };
});